import { Divider } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import classNames from "classnames";
import { ReactComponent as UserLogo } from "../../../images/icons/header-dropdown/user-logo.svg";
import { ReactComponent as ArrowLeftIcon } from "../../../images/icons/elements/arrow-left.svg";
import { matchPath, useLocation } from "react-router-dom";
import {
  disableTitle,
  isNestedRouteWithMenu,
  isNumeric,
  popFromUrl,
} from "./constants";
import AntBtn from "../../atoms/AntBtn";
import { APP_ROUTES } from "../../../system/router/constants";
import { ApplicationState } from "../../../system/reducers";
import { RoleWrapper } from "../../../system/roles/RoleWrapper";
import { Action, Alias, Group } from "../../../system/roles/constants";
import { sidebarData } from "../Sidebar/constants";
import { usePageNavigate } from "../../../system/hooks/usePageNavigate";

const AntPageHeader: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;

  const { name: username } = useSelector(
    (state: ApplicationState) => state.auth,
    shallowEqual,
  );

  const headerTitle = () => {
    const menuItems = sidebarData().filter((item) => !item.subData);
    const subMenuItems = sidebarData().filter((item) => !!item.subData);

    const filter = (data) =>
      data.filter((item) => matchPath(item.route, currentPath))[0]?.label;

    const label = filter(menuItems);
    const subLabel = filter(subMenuItems[0].subData);

    return !!label ? label : subLabel;
  };

  const isDashboardRoute = currentPath === APP_ROUTES.dashboard;
  const isRolesRoute = currentPath.includes("roles");
  const isAccountRoute = currentPath.includes("account");
  const isAccountsRoute = currentPath.includes("accounts");
  const isCalendarsNestedRoute = currentPath.includes("/calendars/");
  const isCustomersNestedRoute = currentPath.includes("/customers/");
  const isGamificationRoute = currentPath.includes("/gamification");
  const isGamificationNestedRoute = currentPath.includes("/gamification/");
  const isPointGamesNestedRoute = currentPath.includes("/point-games/");

  const isActiveBreadcrumb =
    isCustomersNestedRoute ||
    isGamificationRoute ||
    isGamificationNestedRoute ||
    isPointGamesNestedRoute;

  const titleValue =
    headerTitle() ||
    (!isNumeric(popFromUrl(currentPath)) &&
      !(popFromUrl(currentPath) === "new") &&
      !(popFromUrl(currentPath) === "roles") &&
      !isCalendarsNestedRoute &&
      !isGamificationNestedRoute &&
      !isPointGamesNestedRoute &&
      popFromUrl(currentPath));

  const goBackRender = (
    <div className="history-go-back">
      <ArrowLeftIcon />
      {!isCustomersNestedRoute && (
        <span>
          Back
          {(isAccountRoute || isRolesRoute) && " to accounts"}
          {isCalendarsNestedRoute && " to calendars"}
          {isGamificationNestedRoute &&
            (isPointGamesNestedRoute ? " to point games" : " to gamification")}
        </span>
      )}
    </div>
  );

  const { crumbsRoutes, renderCrumbs, navigateBack } = usePageNavigate({
    lastCustomCrumb: location?.state?.username,
  });

  const createAccountBtn = isAccountsRoute && [
    <RoleWrapper
      key={2}
      hasAccess={{
        group: [Group.Admin],
        alias: [Alias.Authentication],
        action: [Action.store],
      }}
    >
      <AntBtn
        text="+ Create admin"
        type="primary"
        linkTo={APP_ROUTES.accountCreate}
      />
    </RoleWrapper>,
    <Divider key={3} type="vertical" className="header-divider-wrapper" />,
  ];

  return (
    <>
      <div className="page-header-gap" />
      <PageHeader
        title={disableTitle(titleValue) && titleValue}
        backIcon={!titleValue && !isDashboardRoute && goBackRender}
        onBack={!titleValue && navigateBack}
        breadcrumb={{
          itemRender: renderCrumbs,
          items: isActiveBreadcrumb && crumbsRoutes,
        }}
        extra={[
          createAccountBtn,
          <div key={5} id="sso-announcements" className="header-announcements-container" />,
          <div key={4} className="header-user-info">
            <UserLogo /> {username}
          </div>,
        ]}
        className={classNames({
          "page-with-menu": isNestedRouteWithMenu(currentPath),
        })}
      />
    </>
  );
};

export default AntPageHeader;
